import React, {useRef, useState} from 'react';
import {Keyboard, Platform, Pressable, StyleSheet, View} from 'react-native';
import {faEnvelope} from '@fortawesome/free-regular-svg-icons/faEnvelope';
import {faKey} from '@fortawesome/free-solid-svg-icons/faKey';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {Formik} from 'formik';
import * as Yup from 'yup';

import Button from '../../components/ui-controls/Button';
import TextInput from '../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {Analytics} from '../../utils/analytics/analytics';
import {
  EventName,
  EventScreenName,
  EventSourceType,
} from '../../utils/analytics/events';

import TogglePasswordVisibleIconButton from './components/TogglePasswordVisibleIconButton';

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email address is required'),
  password: Yup.string().min(8).required('A password is required'),
});

const LoginScreen = ({
  navigation,
  route,
}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_LOGIN>) => {
  const {userStore, shopSelectionStore} = useStores();

  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isPasswordIncorrect, setPasswordIncorrect] = useState(false);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleTogglePasswordVisible = () => {
    setPasswordVisible(!isPasswordVisible);
  };

  const password = useRef(null);

  const onSubmit = async (values: {email: string; password: string}) => {
    setSubmitting(true);
    setPasswordIncorrect(false);
    const success = await userStore.login(values);
    if (success) {
      Analytics.logEvent({
        eventName: EventName.Login,
        parameters: {
          screen_name: EventScreenName.Login,
          source_type: EventSourceType.Button,
          method: 'email',
        },
      });
      userStore.afterLogin();
    } else {
      setPasswordIncorrect(true);
    }
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={{email: route.params.email || '', password: ''}}
      onSubmit={onSubmit}
      validationSchema={LoginSchema}>
      {({
        handleChange,
        handleBlur,
        handleSubmit,
        values,
        errors,
        touched,
        initialValues,
        setFieldValue,
      }) => {
        const intHandlePasswordChange = (val: any) => {
          setPasswordIncorrect(false);
          setFieldValue('password', val);
        };

        const handleInitiatePasswordReset = async () => {
          setSubmitting(true);
          if (Platform.OS === 'android') Keyboard.dismiss();
          await userStore.initiatePasswordReset(values.email, () => {
            setSubmitting(false);
            navigation.navigate(shopSelectionStore.getHomeAppRoute());
          });

          Analytics.logEvent({
            eventName: EventName.PasswordResetRequest,
            parameters: {
              screen_name: EventScreenName.Login,
            },
          });
        };

        return (
          <ContentView testID="login-screen" style={styles.main}>
            <View>
              <TextInput
                autoCapitalize="none"
                autoComplete="email"
                defaultValue={initialValues.email}
                errorMessageWhenTouched={errors.email}
                keyboardType="email-address"
                label="Email Address"
                leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
                onBlur={handleBlur('email')}
                onChangeText={handleChange('email')}
                placeholder="Enter your email"
                touched={touched.email}
              />
            </View>
            <View>
              <TextInput
                autoCapitalize="none"
                autoComplete="password"
                errorMessageWhenTouched={errors.password}
                label="Password"
                leftIcon={<FontAwesomeIcon icon={faKey} />}
                rightIcon={
                  <TogglePasswordVisibleIconButton
                    isPasswordVisible={isPasswordVisible}
                    onPress={handleTogglePasswordVisible}
                  />
                }
                onBlur={handleBlur('password')}
                onChangeText={intHandlePasswordChange}
                onSubmitEditing={handleSubmit}
                placeholder="Enter your password"
                ref={password}
                returnKeyLabel="go"
                returnKeyType="go"
                secureTextEntry={!isPasswordVisible}
                touched={touched.password}
              />
              {!!isPasswordIncorrect && (
                <ValidationMessage style={styles.validationError}>
                  Password incorrect
                </ValidationMessage>
              )}
            </View>
            <Button
              onPress={handleSubmit}
              title="Log in"
              loading={isSubmitting}
              disabled={isSubmitting}
            />
            {!!values.email && !errors.email && (
              <Pressable
                style={styles.resetOuter}
                onPress={handleInitiatePasswordReset}>
                <Text style={styles.resetLink}>Request password reset</Text>
              </Pressable>
            )}
          </ContentView>
        );
      }}
    </Formik>
  );
};

const styles = StyleSheet.create({
  main: {
    flex: 1,
    alignItems: 'stretch',
    ...Platform.select({
      native: {
        justifyContent: 'flex-start',
      },
      web: {
        justifyContent: 'center',
      },
    }),
  },
  validationError: {
    fontSize: 16,
  },
  resetOuter: {
    alignItems: 'center',
    marginTop: 16,
  },
  resetLink: {
    color: theme.colour.text.heading.primary,
    fontSize: 14,
  },
});

export default LoginScreen;
